import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5011/api/v1';

const AUTH_KEYS = {
  accessToken: 'accessToken',
  refreshToken: 'refreshToken',
  user: 'user',
  isLoggedIn: 'isLoggedIn',
  activeRole: 'activeRole',
  orgId: 'org_id',
  userRole: 'userRole',
  legacyUserRole: 'user_role',
};

const AUTH_KEY_LIST = Object.values(AUTH_KEYS);

const getSessionStorage = () => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage;
};

const getLocalStorage = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
};

const readStorageValue = (key) => {
  const storage = getSessionStorage();
  return storage ? storage.getItem(key) : null;
};

const writeStorageValue = (key, value) => {
  const storage = getSessionStorage();
  if (!storage) return;
  if (value == null) {
    storage.removeItem(key);
    return;
  }
  storage.setItem(key, value);
};

const removeStorageValue = (key) => {
  const storage = getSessionStorage();
  if (storage) {
    storage.removeItem(key);
  }
};

export const purgeLegacyAuthStorage = () => {
  const legacyStorage = getLocalStorage();
  if (!legacyStorage) return;
  AUTH_KEY_LIST.forEach((key) => legacyStorage.removeItem(key));
};

purgeLegacyAuthStorage();

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

let activeRequestCount = 0;
const activityListeners = new Set();
const errorListeners = new Set();
let refreshPromise = null;

const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]';

export const getApiErrorMessage = (errorLike, fallback = 'API request failed') => {
  const visited = new Set();

  const unwrap = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      return value.map((item) => unwrap(item)).filter(Boolean).join(', ');
    }
    if (!isPlainObject(value) || visited.has(value)) return '';

    visited.add(value);

    const preferredKeys = ['message', 'error', 'detail', 'description', 'code'];
    for (const key of preferredKeys) {
      const nested = unwrap(value[key]);
      if (nested) return nested;
    }

    for (const nestedValue of Object.values(value)) {
      const nested = unwrap(nestedValue);
      if (nested) return nested;
    }

    return '';
  };

  const message =
    unwrap(errorLike?.response?.data?.message) ||
    unwrap(errorLike?.response?.data?.error) ||
    unwrap(errorLike?.response?.data) ||
    unwrap(errorLike?.message) ||
    unwrap(errorLike);

  return message || fallback;
};

const emitApiActivity = () => {
  activityListeners.forEach((listener) => listener(activeRequestCount));
};

const beginApiActivity = () => {
  activeRequestCount += 1;
  emitApiActivity();
};

const endApiActivity = () => {
  activeRequestCount = Math.max(0, activeRequestCount - 1);
  emitApiActivity();
};

const emitApiError = (payload) => {
  errorListeners.forEach((listener) => listener(payload));
};

const decodeJwtPayload = (token) => {
  if (!token) return null;
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;
    return JSON.parse(window.atob(payloadPart));
  } catch {
    return null;
  }
};

export const getAuthToken = () => readStorageValue(AUTH_KEYS.accessToken);
export const getRefreshToken = () => readStorageValue(AUTH_KEYS.refreshToken);

export const getStoredOrgId = () => readStorageValue(AUTH_KEYS.orgId);
export const setStoredOrgId = (value) => writeStorageValue(AUTH_KEYS.orgId, value == null ? null : String(value));

export const getStoredUser = () => {
  const raw = readStorageValue(AUTH_KEYS.user);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const setStoredUser = (user) => {
  writeStorageValue(AUTH_KEYS.user, user ? JSON.stringify(user) : null);
};

export const getStoredRole = () =>
  readStorageValue(AUTH_KEYS.userRole) ||
  readStorageValue(AUTH_KEYS.activeRole) ||
  getStoredUser()?.active_role ||
  '';

export const setStoredRole = (role) => {
  const value = role ? String(role) : null;
  writeStorageValue(AUTH_KEYS.userRole, value);
  writeStorageValue(AUTH_KEYS.activeRole, value);
  writeStorageValue(AUTH_KEYS.legacyUserRole, value);
};

export const clearAuthSession = () => {
  AUTH_KEY_LIST.forEach(removeStorageValue);
  delete api.defaults.headers.common.Authorization;
};

const applyAccessToken = (token) => {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
};

export const setAuthToken = (token) => {
  writeStorageValue(AUTH_KEYS.accessToken, token || null);
  applyAccessToken(token || null);
};

export const setRefreshToken = (token) => {
  writeStorageValue(AUTH_KEYS.refreshToken, token || null);
};

export const saveAuthSession = ({ accessToken, refreshToken, user }) => {
  setAuthToken(accessToken || null);
  setRefreshToken(refreshToken || null);
  writeStorageValue(AUTH_KEYS.isLoggedIn, accessToken && user ? 'true' : null);
  if (user) {
    setStoredUser(user);
    setStoredRole(user.active_role || user.roles?.[0] || user.role || null);
    if (user.org_id != null) {
      setStoredOrgId(user.org_id);
    }
  }
};

const shouldRefreshAccessToken = (token) => {
  const payload = decodeJwtPayload(token);
  const expSeconds = Number(payload?.exp || 0);
  if (!expSeconds) return false;
  const remainingMs = (expSeconds * 1000) - Date.now();
  return remainingMs <= 60 * 1000;
};

export const logout = async ({ remote = true } = {}) => {
  const refreshToken = getRefreshToken();

  try {
    if (remote && refreshToken) {
      await refreshClient.post('/auth/logout', { refresh_token: refreshToken });
    }
  } catch {
    // Best-effort revoke only.
  } finally {
    clearAuthSession();
  }
};

export const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearAuthSession();
    throw new Error('Refresh token is missing');
  }

  refreshPromise = refreshClient
    .post('/auth/refresh', { refresh_token: refreshToken })
    .then((response) => {
      const nextAccessToken = response?.data?.access_token || null;
      const nextRefreshToken = response?.data?.refresh_token || null;
      const currentUser = getStoredUser() || {};
      const nextUser = response?.data?.user
        ? {
            ...response.data.user,
            active_role: currentUser.active_role || response.data.user.active_role || null,
            active_module: currentUser.active_module || null,
          }
        : currentUser;

      if (!nextAccessToken || !nextRefreshToken) {
        throw new Error('Refresh response is incomplete');
      }

      saveAuthSession({
        accessToken: nextAccessToken,
        refreshToken: nextRefreshToken,
        user: nextUser,
      });

      return nextAccessToken;
    })
    .catch((error) => {
      clearAuthSession();
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

api.interceptors.request.use(
  async (config) => {
    beginApiActivity();

    if (!config?.skipAuthRefresh) {
      const currentToken = getAuthToken();
      if (currentToken && shouldRefreshAccessToken(currentToken)) {
        try {
          await refreshAccessToken();
        } catch {
          // Let the request continue without a token so the caller sees auth failure cleanly.
        }
      }
    }

    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    endApiActivity();
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    endApiActivity();
    return response;
  },
  async (error) => {
    endApiActivity();

    const originalRequest = error?.config || {};
    const isUnauthorized = error?.response?.status === 401;
    const hasRefreshToken = Boolean(getRefreshToken());
    const canRetry = isUnauthorized && hasRefreshToken && !originalRequest._retry && !originalRequest.skipAuthRefresh;

    if (canRetry) {
      originalRequest._retry = true;
      try {
        const nextAccessToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;
        return api(originalRequest);
      } catch {
        // Fall through to emit the original auth error after session cleanup.
      }
    }

    emitApiError({
      message: getApiErrorMessage(error),
      status: error?.response?.status || null,
      url: error?.config?.url || null,
      method: error?.config?.method || null,
    });

    return Promise.reject(error);
  }
);

applyAccessToken(getAuthToken());

export const subscribeApiActivity = (listener) => {
  if (typeof listener !== 'function') {
    return () => {};
  }
  activityListeners.add(listener);
  listener(activeRequestCount);
  return () => {
    activityListeners.delete(listener);
  };
};

export const subscribeApiErrors = (listener) => {
  if (typeof listener !== 'function') {
    return () => {};
  }
  errorListeners.add(listener);
  return () => {
    errorListeners.delete(listener);
  };
};

export const authApi = api;
export const orgApi = api;
export const branchApi = api;
export const employeeApi = api;
export const productionApi = api;
export const qcApi = api;
export const inventoryApi = api;
export const hrApi = api;
export const salesApi = api;
export default api;
